<template>
  <div
    class="metrics-assignment-view"
    :class="{ 'is-exporting-pdf': isExportingPdf }"
  >
    <metrics-class-header
      :title="currentData.assignmentName || null"
      :is-demo-mode="isDemoMode"
      :teacher="teacherName"
      :class-name="currentData.className"
      :graded-date="currentData.gradedDate"
      :students-count="
        isDemoMode
          ? currentData.studentsCount
          : overallInfoData &&
            overallInfoData.counters &&
            overallInfoData.counters.students
      "
      :is-page-loading="isPageLoading"
      :show-regenerate="true"
      @update:isDemoMode="onDemoModeChange"
      @regenerate="onRegenerate"
      @export-pdf="exportToPdf"
      @print="print"
    />

    <div class="metrics-assignment-view__content">
      <metrics-overall-summary
        v-if="
          isDemoMode
            ? currentData.overallSummary
            : insightsData &&
              insightsData.globalSummary &&
              insightsData.globalSummary.overallSummary
        "
        :summary="
          isDemoMode
            ? currentData.overallSummary
            : insightsData.globalSummary.overallSummary
        "
      />

      <metrics-grade-distribution
        :overall-avg="
          isDemoMode
            ? currentData.overallAvg
            : overallInfoData && overallInfoData.overallAvg
        "
        :grades="
          isDemoMode
            ? currentData.studentGroups
            : overallInfoData && overallInfoData.studentGroups
        "
        :clickable="hasStudents"
        @tier-click="openStudentsSidebar"
      />

      <div class="metrics-assignment-view__rubric-row">
        <metrics-rubric-breakdown
          :criteria="
            isDemoMode
              ? currentData.rubricBreakdown && currentData.rubricBreakdown.data
              : (rubricBreakdownData && rubricBreakdownData.data) || []
          "
          :rubric-id="
            isDemoMode
              ? null
              : rubricBreakdownData &&
                rubricBreakdownData.rubric &&
                rubricBreakdownData.rubric.id
          "
          :is-loading="!isDemoMode && isRubricBreakdownLoading"
          @view-rubric="onViewRubric"
        />
        <metrics-score-distribution
          :distribution="
            isDemoMode ? currentData.scoreDistribution : scoreDistribution
          "
          :is-loading="!isDemoMode && isStudentBreakdownLoading"
        />
      </div>

      <metrics-student-performance
        :assignment-id="assignmentId"
        :performance="currentData.studentPerformance"
      />

      <metrics-writing-breakdown
        :breakdown="
          isDemoMode
            ? currentData.writingBreakdown
            : insightsData && insightsData.globalSummary
        "
        :is-loading="!isDemoMode && isInsightsLoading"
      />

      <metrics-student-action-plan
        :intervention="
          isDemoMode
            ? currentData.intervention
            : insightsData && insightsData.intervention
        "
        :extension="
          isDemoMode
            ? currentData.extension
            : insightsData && insightsData.extension
        "
        :reteach-priority="
          isDemoMode
            ? currentData.reteachPriority
            : insightsData &&
              insightsData.globalSummary &&
              insightsData.globalSummary.reteachPriority
        "
        :celebrate-publicly="
          isDemoMode
            ? currentData.celebratePublicly
            : insightsData &&
              insightsData.globalSummary &&
              insightsData.globalSummary.celebratePublicly
        "
        :is-loading="!isDemoMode && isInsightsLoading"
      />
    </div>
    <metrics-pdf-overlay v-if="isExportingPdf" />

    <students-sidebar
      v-model="studentsSidebarVisible"
      mode="metrics"
      :title="studentsSidebarTitle"
      :metrics-students="studentsSidebarList"
      :search-key="studentsSidebarTier"
    />
  </div>
</template>

<script>
import MetricsClassHeader from "./components/class/metrics_class_header.vue";
import MetricsGradeDistribution from "./components/shared/metrics_grade_distribution.vue";
import StudentsSidebar from "@/components/students/students_sidebar.vue";
import MetricsRubricBreakdown from "./components/assignment/metrics_rubric_breakdown.vue";
import MetricsScoreDistribution from "./components/shared/metrics_score_distribution.vue";
import MetricsStudentPerformance from "./components/assignment/metrics_student_performance.vue";
import MetricsStudentActionPlan from "./components/assignment/metrics_student_action_plan.vue";
import MetricsWritingBreakdown from "./components/shared/metrics_writing_breakdown.vue";
import MetricsOverallSummary from "./components/class/metrics_overall_summary.vue";
import {
  metricsAssignmentDemoData,
  metricsAssignmentEmptyData,
} from "./demo_data/assignment.js";
import {
  getMetricsOverallInfo,
  getMetricsStudentBreakdown,
  getMetricsAssignmentInsights,
  getMetricsRubricBreakdown,
  getMetricsStudentGrowth,
} from "@/api/requests/metrics.js";
import { openRubricPreview } from "@/components/dashboard/components/open_rubric_preview.js";
import metricsViewMixin from "@/mixins/metricsViewMixin.js";
import MetricsPdfOverlay from "./components/shared/metrics_pdf_overlay.vue";
import { getScoreTier } from "@/utils/metricsUtils";

const LS_KEY_DEMO_MODE = "metrics-is-demo-mode";

export default {
  name: "MetricsAssignmentView",

  components: {
    MetricsClassHeader,
    MetricsGradeDistribution,
    StudentsSidebar,
    MetricsRubricBreakdown,
    MetricsScoreDistribution,
    MetricsStudentPerformance,
    MetricsStudentActionPlan,
    MetricsWritingBreakdown,
    MetricsOverallSummary,
    MetricsPdfOverlay,
  },

  mixins: [metricsViewMixin],

  inject: ["workspacesProvider"],

  data() {
    const savedDemo = localStorage.getItem(LS_KEY_DEMO_MODE);

    return {
      isDemoMode: savedDemo !== null ? JSON.parse(savedDemo) : false,
      isExportingPdf: false,
      enteredInDemoMode: false,
      overallInfoData: null,
      studentBreakdownData: null,
      insightsData: null,
      rubricBreakdownData: null,
      assignmentFolderData: null,
      classFolderData: null,
      isRubricBreakdownLoading: false,
      isStudentBreakdownLoading: false,
      isInsightsLoading: false,
      studentsSidebarVisible: false,
      studentsSidebarTier: null,
      studentGrowthData: [],
    };
  },

  computed: {
    classId() {
      return this.$route.params.classId;
    },

    assignmentId() {
      return this.$route.params.assignmentId;
    },

    assignmentFolder() {
      return (
        this.assignmentFolderData ||
        this.getFolderFromProvider(this.assignmentId)
      );
    },

    classFolder() {
      return this.classFolderData || this.getFolderFromProvider(this.classId);
    },

    pdfFileName() {
      return "metrics-assignment";
    },

    isPageLoading() {
      return (
        this.isRubricBreakdownLoading ||
        this.isStudentBreakdownLoading ||
        this.isInsightsLoading ||
        this.overallInfoData === null
      );
    },

    pdfSubjectName() {
      const name = this.currentData.assignmentName;
      return name ? `(${name})` : "";
    },

    studentsSidebarTitle() {
      const TIER_KEYS = {
        high: "metrics.tierSidebarHigh",
        medium: "metrics.tierSidebarMedium",
        low: "metrics.tierSidebarLow",
      };
      const key = TIER_KEYS[this.studentsSidebarTier];
      return key ? this.$t(key) : this.$t("metrics.tierSidebarDefault");
    },

    studentsSidebarList() {
      const students = this.studentGrowthData;
      if (!this.studentsSidebarTier) return students;
      return students.filter(
        (s) => getScoreTier(s.avgGrade) === this.studentsSidebarTier
      );
    },

    hasStudents() {
      return this.studentGrowthData.length > 0;
    },

    scoreDistribution() {
      if (!this.studentBreakdownData) return null;
      return { rows: this.studentBreakdownData.data };
    },

    currentData() {
      if (this.isDemoMode) return metricsAssignmentDemoData;
      return {
        ...metricsAssignmentEmptyData,
        assignmentName: this.assignmentFolder?.name || null,
        className: this.classFolder?.name || null,
        gradedDate: this.assignmentFolder?.updatedAt
          ? new Date(this.assignmentFolder.updatedAt).toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric", year: "numeric" }
            )
          : null,
      };
    },
  },

  mounted() {
    this.enteredInDemoMode = this.isDemoMode;
    if (this.isDemoMode) {
      this.fetchStudentGrowth();
    } else {
      this.fetchStudentBreakdown();
      this.fetchOverallInfo();
      this.fetchInsights();
      this.fetchRubricBreakdown();
      this.fetchStudentGrowth();
      this.loadFolders();
    }
  },

  methods: {
    async loadFolders() {
      const cls = await this.fetchEntityFolder(this.classId);
      if (cls) this.classFolderData = cls;
    },

    async fetchStudentBreakdown() {
      if (!this.entityId) return;
      this.isStudentBreakdownLoading = true;
      try {
        const result = await getMetricsStudentBreakdown({
          entityId: this.entityId,
          assignmentId: this.assignmentId,
        });
        this.studentBreakdownData = result?.data || null;
      } catch {
        this.studentBreakdownData = null;
      } finally {
        this.isStudentBreakdownLoading = false;
      }
    },

    async fetchRubricBreakdown() {
      if (!this.entityId) return;
      this.isRubricBreakdownLoading = true;
      try {
        const result = await getMetricsRubricBreakdown({
          entityId: this.entityId,
          assignmentId: this.assignmentId,
        });
        this.rubricBreakdownData = result?.data || null;
      } catch {
        this.rubricBreakdownData = null;
      } finally {
        this.isRubricBreakdownLoading = false;
      }
    },

    async fetchInsights() {
      if (!this.entityId) return;
      this.isInsightsLoading = true;
      try {
        const result = await getMetricsAssignmentInsights({
          entityId: this.entityId,
          assignmentId: this.assignmentId,
        });
        this.insightsData = result?.data || null;
      } catch {
        this.insightsData = null;
      } finally {
        this.isInsightsLoading = false;
      }
    },

    async fetchOverallInfo() {
      if (!this.entityId) return;
      try {
        const result = await getMetricsOverallInfo({
          entityId: this.entityId,
          assignmentIds: [Number(this.assignmentId)],
        });
        this.overallInfoData = result?.data || null;
      } catch {
        this.overallInfoData = null;
      }
    },

    onDemoModeChange(value) {
      this.isDemoMode = value;
      localStorage.setItem(LS_KEY_DEMO_MODE, JSON.stringify(value));
      if (!value) {
        if (this.enteredInDemoMode) {
          this.$router.push(this.metricsOverviewPath);
          return;
        }
        this.fetchStudentBreakdown();
        this.fetchOverallInfo();
        this.fetchInsights();
        this.fetchRubricBreakdown();
        this.loadFolders();
      }
    },

    async fetchStudentGrowth() {
      if (this.isDemoMode) {
        this.studentGrowthData = metricsAssignmentDemoData.studentGrowthData;
        return;
      }
      if (!this.entityId || !this.classId || !this.assignmentId) return;
      try {
        const result = await getMetricsStudentGrowth({
          entityId: this.entityId,
          assignmentIds: [Number(this.assignmentId)],
        });
        this.studentGrowthData = result?.data?.data || [];
      } catch {
        this.studentGrowthData = [];
      }
    },

    onRegenerate() {
      this.overallInfoData = null;
      this.fetchStudentBreakdown();
      this.fetchOverallInfo();
      this.fetchInsights();
      this.fetchRubricBreakdown();
      this.fetchStudentGrowth();
    },

    openStudentsSidebar(tier) {
      this.studentsSidebarTier = tier;
      this.studentsSidebarVisible = true;
    },

    onViewRubric(rubricId) {
      openRubricPreview({ id: rubricId });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./components/shared/metrics_card";

.metrics-assignment-view {
  @include metrics-view;
  gap: 16px;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
  }

  &__rubric-row {
    display: flex;
    gap: 16px;
    padding: 0 24px;

    @media (max-width: 1200px) {
      flex-direction: column;
    }
  }
}
</style>
